import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ErrandCard } from "../components/ErrandCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CATEGORIES } from "../lib/constants";
import { Search, PlusCircle, Loader2 } from "lucide-react";

export default function Feed() {
  const { user } = useAuth();
  const [errands, setErrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/errands", { params: { q: q || undefined, category: cat, status: "open" } });
      setErrands(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const deleteErrand = async (id) => {
    try {
      await api.delete(`/errands/${id}`);
      setErrands((prev) => prev.filter((e) => e.id !== id));
      toast.success("Errand deleted");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20]">
            Hey {user?.display_name} 👋
          </h1>
        </div>
        <Link to="/post"><Button data-testid="feed-post-btn" className="rounded-full font-bold px-6 bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
          <PlusCircle size={18} className="mr-2" /> Request an Errand
        </Button></Link>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input data-testid="feed-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search errands…"
            className="pl-11 rounded-full h-12 border-gray-200 bg-white" />
        </div>
        <Button data-testid="feed-search-btn" type="submit" variant="outline" className="rounded-full h-12 px-6 font-semibold border-2">Search</Button>
      </form>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
        <button data-testid="cat-all" onClick={() => setCat("all")}
          className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cat === "all" ? "bg-primary text-white" : "bg-white border border-gray-200 text-[#4A5361]"}`}>
          All
        </button>
        {CATEGORIES.map((c) => (
          <button key={c.value} data-testid={`cat-${c.value}`} onClick={() => setCat(c.value)}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cat === c.value ? "bg-primary text-white" : "bg-white border border-gray-200 text-[#4A5361]"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>
      ) : errands.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
          <p className="font-heading font-bold text-xl text-[#1A1D20]">No open errands yet</p>
          <p className="text-[#6B7280] mt-1 mb-5">Be the first to post one!</p>
          <Link to="/post"><Button className="rounded-full font-bold bg-primary hover:bg-[#FF8D70]">Request an Errand</Button></Link>
        </div>
      ) : (
        <div data-testid="errand-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
          {errands.map((e, i) => (
            <ErrandCard key={e.id} errand={e} index={i} wide={e.description?.length > 120 && i % 5 === 0}
              currentUserId={user?.id} onDelete={deleteErrand} />
          ))}
        </div>
      )}
    </div>
  );
}
